import React from "react";
import { withFormik, Form, Field } from "formik";
import * as Yup from "yup";
import { FormGroup, Label, Button } from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";
import axios from "../../services/api/api";
import { checkNameLengthAndCharacters } from "../../Utilities/checkerFunctions";
import { TOAST_CONFIG } from "../../Utilities/toastConfig";
import { compareByName } from "../../Utilities/sortHelper";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";

const CreateStudentForm = ({
  errors,
  touched,
  isSubmitting,
  dismissModal,
  studentClasses,
}) => {
  const intl = useIntl();
  return (
    <div className="col-12 col-sm-12">
      <Form>
        <FormGroup>
          <Label>
            <FormattedMessage id="Name" />
          </Label>
          <Field
            className={`form-control ${
              errors.name && touched.name && "is-invalid"
            }`}
            type="input"
            name="name"
            placeholder={intl.formatMessage({ id: "StudentName" })}
          />
          {errors.name && touched.name && (
            <div className="invalid-feedback">{errors.name}</div>
          )}
        </FormGroup>
        <FormGroup>
          <Label>
            <FormattedMessage id="EmailAddress" />
          </Label>
          <Field
            className={`form-control ${
              errors.email && touched.email && "is-invalid"
            }`}
            type="email"
            name="email"
            placeholder={intl.formatMessage({ id: "EmailAddress" })}
          />
          {errors.email && touched.email && (
            <div className="invalid-feedback">{errors.email}</div>
          )}
        </FormGroup>

        <FormGroup>
          <Label for="studentClassSelect">
            <FormattedMessage id="ChooseClass" />
          </Label>
          <Field
            component="select"
            name="studentClassId"
            id="studentClassSelect"
            className={`form-control ${
              errors.studentClassId && touched.studentClassId && "is-invalid"
            }`}
          >
            <option value="-1">{intl.formatMessage({ id: "SelectClassFromList" })}</option>
            {[...studentClasses].sort(compareByName).map((c) => {
              return (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              );
            })}
          </Field>
          {errors.studentClassId && touched.studentClassId && (
            <div className="invalid-feedback">{errors.studentClassId}</div>
          )}
        </FormGroup>
        <div className="d-flex justify-content-end gap-2">
          <Button
            color="secondary"
            outline
            type="button"
            onClick={dismissModal}
          >
            <FormattedMessage id="Cancel" />
          </Button>
          <Button color="primary" outline type="submit" disabled={isSubmitting}>
            <FormattedMessage id={isSubmitting ? "Wait" : "Add"} />
          </Button>
        </div>
      </Form>
    </div>
  );
};

const FormikForm = withFormik({
  mapPropsToValues({ name, email, studentClassId, classes }) {
    return {
      email: email || "",
      name: name || "",
      studentClassId: studentClassId || -1,
      studentClasses: classes || [],
    };
  },

  validationSchema: Yup.object().shape({
    email: Yup.string()
      .email(
        <FormattedMessage id="Views.Teacher.CreateStudentForm.Error.RequireEmail" />
      )
      .required(
        <FormattedMessage id="Views.Teacher.CreateStudentForm.Error.RequireEmail" />
      ),
    name: Yup.string().required(
      <FormattedMessage id="Views.Teacher.CreateStudentForm.Error.RequireName" />
    ),
  }),

  handleSubmit(values, { props, resetForm, setErrors, setSubmitting }) {
    if (values.studentClassId === -1 || values.studentClassId === "-1") {
      setErrors({
        studentClassId: (
          <FormattedMessage id="Views.Teacher.CreateStudentForm.Error.SelectClass" />
        ),
      });
      setSubmitting(false);
      return;
    }
    if (checkNameLengthAndCharacters(values.name) !== true) {
      toast.warn(checkNameLengthAndCharacters(values.name));
      setSubmitting(false);
    } else {
      axios
        .post("/student", {
          email: values.email,
          name: values.name,
          classId: values.studentClassId,
        })
        .then((response) => {
          toast.success(
            <FormattedMessage id="Tostify.Message.AddedStudent" />,
            TOAST_CONFIG
          );

          var newStudent = {
            email: response.data.result.email,
            id: response.data.result.id,
            name: response.data.result.name,
          };
          props.updateStudents(newStudent, response.data.result.classId);
          resetForm();
          props.dismissModal();
        })
        .catch((err) => {
          setErrors({
            studentClassId: formatServiceError(props.intl, err?.response?.data),
          });
        })
        .finally(() => setSubmitting(false));
    }
  },
})(CreateStudentForm);

const CreateStudentFormWithIntl = (props) => {
  const intl = useIntl();

  return <FormikForm {...props} intl={intl} />;
};

export default CreateStudentFormWithIntl;
